import { useState } from "react";
import { useNavigate } from "react-router-dom";
import useSWR from "swr";
import { ChevronRight, Plus, Server } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { EmptyState, PageHeader, Stat, StatusBadge } from "@/components/Common";
import { KeyRevealDialog } from "@/components/KeyDialog";
import { api, errorText, fetcher } from "@/lib/api";
import { fmtNum, relTime } from "@/lib/format";

function RegisterDialog({ open, onOpenChange, onCreated }) {
  const [name, setName] = useState("Site server");
  const [site, setSite] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/devices", { name, site });
      onCreated(data);
      onOpenChange(false);
      toast.success("Server registered");
    } catch (err) {
      toast.error(errorText(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md rounded-2xl" data-testid="register-device-dialog">
        <form onSubmit={submit} className="space-y-4">
          <DialogHeader>
            <DialogTitle>Register a server</DialogTitle>
            <DialogDescription>Creates a device key the Edge Sender uses to sign every upload.</DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label htmlFor="dev-name">Name</Label>
            <Input id="dev-name" value={name} onChange={(e) => setName(e.target.value)} required maxLength={80} className="rounded-xl" data-testid="register-device-name-input" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="dev-site">Location (optional)</Label>
            <Input id="dev-site" value={site} onChange={(e) => setSite(e.target.value)} maxLength={120} placeholder="e.g. Control room, KM 12" className="rounded-xl" data-testid="register-device-site-input" />
          </div>
          <DialogFooter>
            <Button type="submit" disabled={busy} className="rounded-full" data-testid="device-key-generate-button">Register and create key</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function DeviceRow({ d }) {
  const nav = useNavigate();
  const hb = d.heartbeat || {};
  return (
    <button onClick={() => nav(`/connections/${d.id}`)} data-testid={`device-row-${d.id}`}
      className="flex w-full items-center gap-4 px-5 py-4 text-left transition-colors hover:bg-secondary/60">
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-secondary"><Server className="h-5 w-5 text-muted-foreground" /></div>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-medium">{d.name}</span>
          <StatusBadge status={d.status} testId={`edge-server-status-badge-${d.id}`} />
        </div>
        <div className="mt-0.5 truncate text-xs text-muted-foreground">
          {d.site || "No location"} · {hb.hostname || "host unknown"} · heartbeat {relTime(d.last_heartbeat_at)}
        </div>
      </div>
      <div className="hidden text-right sm:block">
        <div className="text-sm font-medium num">{fmtNum(d.rows_received_total)}</div>
        <div className="text-xs text-muted-foreground">rows received</div>
      </div>
      <div className="hidden text-right md:block">
        <div className="text-sm font-medium">{relTime(d.last_data_at)}</div>
        <div className="text-xs text-muted-foreground">last data</div>
      </div>
      <ChevronRight className="h-4 w-4 text-muted-foreground" />
    </button>
  );
}

export default function Connections() {
  const { data: devices, mutate } = useSWR("/devices", fetcher, { refreshInterval: 10000 });
  const [open, setOpen] = useState(false);
  const [created, setCreated] = useState(null);
  const list = devices || [];
  const online = list.filter((d) => ["online", "syncing", "paused"].includes(d.status)).length;
  const total = list.reduce((a, d) => a + (d.rows_received_total || 0), 0);

  return (
    <div>
      <PageHeader title="Connections" subtitle="Windows servers that push MySQL data to this dashboard over outbound HTTPS. No inbound ports are opened on the server.">
        <Button onClick={() => setOpen(true)} className="rounded-full" data-testid="connection-centre-register-device-button">
          <Plus className="mr-1.5 h-4 w-4" /> Register server
        </Button>
      </PageHeader>
      <div className="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Stat label="Servers" value={list.length} testId="stat-servers" />
        <Stat label="Online now" value={`${online} / ${list.length}`} testId="stat-online" />
        <Stat label="Rows received" value={fmtNum(total)} testId="stat-rows-received" />
      </div>
      {devices && list.length === 0 ? (
        <EmptyState icon={Server} title="No servers yet" testId="devices-empty-state"
          text="Register your Windows server to get a device key, then install the Edge Sender on it using the Setup guide.">
          <Button onClick={() => setOpen(true)} className="rounded-full" data-testid="empty-register-device-button">Register server</Button>
        </EmptyState>
      ) : (
        <div className="surface divide-y divide-border/70 overflow-hidden" data-testid="devices-list">
          {list.map((d) => <DeviceRow key={d.id} d={d} />)}
        </div>
      )}
      <RegisterDialog open={open} onOpenChange={setOpen} onCreated={(r) => { setCreated(r); mutate(); }} />
      <KeyRevealDialog open={!!created} onClose={() => setCreated(null)} deviceKey={created?.device_key} deviceName={created?.device?.name} />
    </div>
  );
}
